import React, { useEffect, useRef } from 'react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { colors, typography } from '../../../styles/theme';
import { FILTER_ACCENT, FILTER_ACCENT_14 } from '../FilterPanel/FilterPanel.styles';
import SearchIdleSuggestions from './SearchIdleSuggestions';

const TYPE_LABELS = {
  city: 'Ciudad',
  town: 'Localidad',
  village: 'Pueblo',
  suburb: 'Barrio',
  neighbourhood: 'Barrio',
  county: 'Departamento',
  state: 'Provincia',
  municipality: 'Municipio',
  road: 'Calle',
  house: 'Dirección',
  residential: 'Zona',
  quarter: 'Zona',
};

function parseDisplayName(displayName) {
  const parts = displayName.split(',').map((p) => p.trim());
  const filtered = parts.filter((p) => p !== 'Argentina');
  const name = filtered[0] || displayName;
  const sub = filtered.slice(1).join(', ');
  return { name, sub };
}

function getTypeLabel(type, cls) {
  return TYPE_LABELS[type] || TYPE_LABELS[cls] || 'Lugar';
}

/**
 * Dropdown de resultados de búsqueda geográfica.
 * @param {boolean} [props.mixedSearch] Si true, Nominatim vacío no es “sin resultados”:
 *   el usuario todavía puede confirmar con Enter (lenguaje natural).
 * @param {boolean} [props.nlMode] Heurística: descripción NL (no geocode en vivo).
 * @param {string[]} [props.suggestedQueries] Chips del Estado 1 (foco sin escribir).
 * @param {string[]} [props.recentQueries] Recientes; mismo slot que sugeridas.
 * @param {(query: string) => void} [props.onSelectQuery] Completa el input y ejecuta.
 * @param {(query: string) => void} [props.onForgetQuery] Quita una reciente del historial.
 */
function PlaceSearchResults({
  results,
  loading,
  error,
  query,
  minChars,
  hoveredIndex,
  onHoverIndexChange,
  onSelectResult,
  disabled = false,
  idleHint = 'Buscá una dirección o localidad en Argentina',
  mixedSearch = false,
  nlMode = false,
  suggestedQueries = [],
  recentQueries = [],
  onSelectQuery,
  onForgetQuery,
}) {
  const showList = !disabled && results.length > 0;
  const nominatimEmpty = !disabled && !loading && !error && query.trim().length >= minChars && results.length === 0;
  const showEmpty = nominatimEmpty && !mixedSearch;
  const showEnterHint = mixedSearch && !loading && !error && !showList && (
    nlMode || nominatimEmpty
  ) && query.trim().length >= minChars;
  const showHint = !disabled && !loading && !error && query.trim().length < minChars && query.length > 0;
  const showIdle = !disabled && !showList && !loading && !error && !showHint && !showEmpty && !showEnterHint;
  const listRef = useRef(null);

  useEffect(() => {
    if (hoveredIndex < 0 || !listRef.current) return undefined;
    const list = listRef.current;
    const row = list.querySelector(`[data-place-index="${hoveredIndex}"]`);
    if (!row) return undefined;
    const listRect = list.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    if (rowRect.bottom > listRect.bottom) {
      list.scrollTop += rowRect.bottom - listRect.bottom + 4;
    } else if (rowRect.top < listRect.top) {
      list.scrollTop -= listRect.top - rowRect.top + 4;
    }
    return undefined;
  }, [hoveredIndex]);

  if (disabled) {
    return (
      <div style={styles.status}>
        Inicia sesión para usar el buscador de lugares.
      </div>
    );
  }

  return (
    <>
      {/* Solo con fetch real de lugares en curso — nunca para NL pendiente de Enter. */}
      {loading && !nlMode && <div style={styles.status}>Buscando…</div>}

      {!loading && error && (
        <div style={styles.statusError}>{error}</div>
      )}

      {showHint && (
        <div style={styles.status}>
          Escribí al menos {minChars} caracteres
        </div>
      )}

      {showEmpty && (
        <div style={styles.status}>
          Sin resultados para{' '}
          <strong style={styles.statusHighlight}>&quot;{query}&quot;</strong>
        </div>
      )}

      {showEnterHint && (
        <div style={styles.enterHint} role="status">
          <span style={styles.enterHintText}>Presioná</span>
          <span style={styles.enterKey} aria-hidden>
            <KeyboardReturnIcon style={styles.enterKeyIcon} />
            <span>Enter</span>
          </span>
          <span style={styles.enterHintText}>para buscar lotes</span>
        </div>
      )}

      {showList && (
        <div ref={listRef} style={styles.list} role="presentation">
          {results.map((result, idx) => {
            const { name, sub } = parseDisplayName(result.display_name);
            const typeLabel = getTypeLabel(result.type, result.class);
            const isHovered = idx === hoveredIndex;

            return (
              <div
                key={result.place_id}
                data-place-index={idx}
                role="option"
                aria-selected={isHovered}
                style={{
                  ...styles.item,
                  ...(isHovered ? styles.itemHover : {}),
                }}
                onClick={() => onSelectResult(result)}
                onMouseEnter={() => onHoverIndexChange(idx)}
                onMouseLeave={() => onHoverIndexChange(-1)}
              >
                <LocationOnIcon style={styles.itemIcon} />
                <div style={styles.itemText}>
                  <div style={styles.itemName} title={name}>{name}</div>
                  {sub && <div style={styles.itemSub} title={sub}>{sub}</div>}
                </div>
                <span style={styles.badge}>{typeLabel}</span>
              </div>
            );
          })}
        </div>
      )}

      {showIdle && mixedSearch && (
        (suggestedQueries.length > 0 || recentQueries.length > 0)
          ? (
            <SearchIdleSuggestions
              suggestions={suggestedQueries}
              recentQueries={recentQueries}
              onSelectQuery={onSelectQuery}
              onForgetQuery={onForgetQuery}
            />
          )
          : (
            <div style={styles.status}>{idleHint}</div>
          )
      )}

      {showIdle && !mixedSearch && (
        <div style={styles.status}>
          {idleHint}
        </div>
      )}
    </>
  );
}

const styles = {
  status: {
    padding: '16px 14px',
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.sm,
    lineHeight: typography.lineHeight.normal,
    color: colors.textSecondary,
    textAlign: 'center',
  },
  statusError: {
    padding: '16px 14px',
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.sm,
    lineHeight: typography.lineHeight.normal,
    color: colors.error,
    textAlign: 'center',
  },
  statusHighlight: {
    color: colors.textPanelDescription,
    fontWeight: typography.fontWeight.medium,
  },
  enterHint: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    padding: '18px 14px',
  },
  enterHintText: {
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.sm,
    lineHeight: typography.lineHeight.normal,
    color: colors.textSecondary,
  },
  enterKey: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '3px 8px',
    borderRadius: '6px',
    border: `1px solid ${colors.border}`,
    backgroundColor: FILTER_ACCENT_14,
    color: FILTER_ACCENT,
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.tight,
  },
  enterKeyIcon: {
    fontSize: '14px',
    width: '14px',
    height: '14px',
  },
  list: {
    maxHeight: '260px',
    overflowY: 'auto',
    padding: '4px 0',
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '9px 14px',
    borderLeft: '2px solid transparent',
    cursor: 'pointer',
    transition: 'background-color 0.12s ease',
    scrollMarginTop: '4px',
    scrollMarginBottom: '4px',
  },
  itemHover: {
    backgroundColor: FILTER_ACCENT_14,
    borderLeftColor: FILTER_ACCENT,
  },
  itemIcon: {
    color: FILTER_ACCENT,
    flexShrink: 0,
    marginTop: '1px',
    fontSize: '18px',
  },
  itemText: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.normal,
    color: colors.textPanelDescription,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemSub: {
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.xs,
    lineHeight: typography.lineHeight.tight,
    color: 'rgba(255,255,255,0.42)',
    marginTop: '2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  badge: {
    fontFamily: typography.fontFamily.primary,
    fontSize: typography.fontSize.xs,
    color: FILTER_ACCENT,
    backgroundColor: FILTER_ACCENT_14,
    borderRadius: '4px',
    padding: '2px 6px',
    flexShrink: 0,
    alignSelf: 'center',
    whiteSpace: 'nowrap',
    fontWeight: typography.fontWeight.medium,
  },
};

export default PlaceSearchResults;
